import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography, Box, Container, Paper, Button, Grid,
  Card, CardContent, CardActions, CircularProgress, Alert, Chip,
} from '@mui/material';
import { Edit, Send, PersonAdd } from '@mui/icons-material';
import axios from '../utils/api';

interface LawyerData {
  firstName: string;
  lastName: string;
  registrationType?: string;
}

const LawyerDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = (searchParams.get('email') || '').trim().toLowerCase();
  const token = (searchParams.get('token') || '').trim();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lawyer, setLawyer] = useState<LawyerData | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!email || !token) {
        setLoadError('Email and token are required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post('/api/lawyers/verify-edit-token', {
          email,
          token,
        });
        const data = response.data.lawyer;
        setLawyer({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          registrationType: data.registrationType || 'directory',
        });
      } catch (err: any) {
        setLoadError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to verify token.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [email, token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography>Loading your dashboard...</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
        <Button variant="outlined" href="/register/edit-request">
          Request a New Token
        </Button>
      </Container>
    );
  }

  if (!lawyer) return null;

  const isReferralOnly = lawyer.registrationType === 'referral';
  const editFormUrl = `/register/edit-form?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
  const referralRequestUrl = `/register/referral-request?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
  const upgradeUrl = `/register/edit-form?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}&upgradeToDirectory=true`;

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Welcome back, {lawyer.firstName} {lawyer.lastName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isReferralOnly ? (
              <Chip label="Requester Only" color="warning" />
            ) : (
              <Chip label="Registry Member" color="primary" />
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Request a Client Referral card */}
          <Grid item xs={12} sm={isReferralOnly ? 4 : 6}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Send color="primary" />
                  <Typography variant="h6">Help Find a Lawyer for Someone Else</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Find lawyers in a particular practice area in a specific jurisdiction
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(referralRequestUrl)}
                >
                  Submit Attorney Search Request
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Join the Directory card — referral-only lawyers only */}
          {isReferralOnly && (
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonAdd color="primary" />
                    <Typography variant="h6">Register to be Included in the Directory to Potential Clients</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Complete your full registration for potential clients to receive your information
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(upgradeUrl)}
                  >
                    Complete Full Registration
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          )}
          
          {/* Edit My Profile card */}
          <Grid item xs={12} sm={isReferralOnly ? 4 : 6}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Edit color="primary" />
                  <Typography variant="h6">Edit My Profile</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {isReferralOnly
                    ? 'Update your contact details'
                    : 'Update your registration information'}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(editFormUrl)}
                >
                  Edit Profile
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default LawyerDashboard;
